import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

const PRESETS = [
  { days: 7, label: "7 giorni" },
  { days: 30, label: "30 giorni" },
  { days: 90, label: "90 giorni" },
  { days: 365, label: "Anno" },
];

const fmtDay = (iso) => {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
};

export function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = useCallback(async () => {
    try {
      const params = custom && from ? `from_date=${from}${to ? `&to_date=${to}` : ""}` : `days=${days}`;
      const { data: d } = await api.get(`/admin/analytics?${params}`);
      setData(d);
    } catch { toast.error("Errore caricamento analytics"); }
  }, [days, custom, from, to]);
  useEffect(() => { load(); }, [load]);

  const kpis = data ? [
    ["Visualizzazioni di pagina", data.totals?.page_view || 0],
    ["Sessioni", data.sessions_total ?? 0],
    ["Form inviati", data.totals?.form_submit || 0],
    ["Prodotti visti", data.totals?.product_view || 0],
    ["Iscritti newsletter (totale)", data.newsletter_count || 0],
  ] : [];

  const biz = data?.biz ? [
    ["Prenotazioni", data.biz.bookings],
    ["Contatti / lead", data.biz.contacts],
    ["Richieste preventivo", data.biz.estimates + (data.biz.inquiries || 0)],
    ["Ordini Shop pagati", data.biz.shop_orders_paid],
    ["Shop the Project pagati", data.biz.stp_orders_paid],
    ["Download Library", data.biz.library_downloads],
    ["Nuovi iscritti newsletter", data.biz.newsletter_new],
  ] : [];

  const daily = data?.daily_views || [];
  const sessionsByDay = Object.fromEntries((data?.daily_sessions || []).map((d) => [d.day, d.count]));
  const maxDaily = Math.max(1, ...daily.map((d) => d.count));
  const sources = data?.sources || [];
  const btn = "min-h-[44px] px-4 text-[11px] tracking-[0.12em] uppercase border transition-colors";

  return (
    <div className="space-y-5" data-testid="analytics-panel">
      {/* Filtri periodo: touch friendly */}
      <div className="flex flex-wrap gap-2 items-end">
        {PRESETS.map((p) => (
          <button key={p.days} onClick={() => { setDays(p.days); setCustom(false); }}
            className={`${btn} ${!custom && days === p.days ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}
            data-testid={`analytics-days-${p.days}`}>
            {p.label}
          </button>
        ))}
        <button onClick={() => setCustom(true)}
          className={`${btn} ${custom ? "bg-ink text-bone border-ink" : "border-hair hover:border-ink"}`}
          data-testid="analytics-custom">
          Personalizzato
        </button>
        {custom && (
          <div className="flex gap-2 items-center flex-wrap">
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="rounded-none h-11 w-40" data-testid="analytics-from" />
            <span className="text-taupe text-xs">→</span>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="rounded-none h-11 w-40" data-testid="analytics-to" />
          </div>
        )}
        {data && <span className="text-xs text-taupe ml-auto">Periodo: {data.from_date} → {data.to_date}</span>}
      </div>

      {!data ? <p className="text-taupe text-sm">Caricamento…</p> : (
        <>
          {/* KPI traffico: 2 colonne su mobile, mai schiacciate */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {kpis.map(([l, v]) => (
              <div key={l} className="bg-white border border-hair p-4 md:p-6 min-w-0">
                <div className="eyebrow truncate">{l}</div>
                <div className="font-serif text-2xl md:text-4xl mt-2">{v}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-taupe" data-testid="analytics-method-note">
            Visualizzazioni di pagina pubbliche, al netto di amministratori autenticati e traffico automatico riconosciuto
            {data.excluded_internal ? ` (${data.excluded_internal} eventi interni/test esclusi nel periodo)` : ""}.
            I ricaricamenti di pagina sono inclusi nelle visualizzazioni. Le sessioni usano un identificativo anonimo che scade
            alla chiusura della scheda (misurabili solo sul traffico raccolto da fine settembre 2026). I visitatori unici non
            sono misurabili con il tracciamento attuale e non vengono stimati.
          </p>

          {/* KPI di business reali dal database */}
          <div className="bg-white border border-hair p-4 md:p-6">
            <div className="eyebrow mb-4">Attività nel periodo (dati reali dal database)</div>
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
              {biz.map(([l, v]) => (
                <div key={l} className="border border-hair p-3 min-w-0">
                  <div className="text-[10px] tracking-[0.1em] uppercase text-taupe leading-tight">{l}</div>
                  <div className="font-serif text-xl md:text-2xl mt-1.5">{v}</div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-taupe mt-3">Conteggi su record reali (esclusi solo i cestinati). Gli elementi archiviati restano conteggiati: l'archiviazione non altera le statistiche storiche.</p>
          </div>

          {/* Grafici: card impilate su mobile, affiancate su desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
            <div className="bg-white border border-hair p-4 md:p-6 min-w-0">
              <div className="eyebrow mb-4">Visualizzazioni di pagina · per giorno</div>
              <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="analytics-daily">
                {daily.map((d) => (
                  <div key={d.day} className="flex items-center gap-2 text-xs">
                    <span className="text-taupe w-12 shrink-0 font-mono">{fmtDay(d.day)}</span>
                    <div className="flex-1 bg-hair h-2.5 relative min-w-0">
                      <div className="absolute inset-y-0 left-0 bg-gold" style={{ width: `${(d.count / maxDaily) * 100}%` }} />
                    </div>
                    <span className="w-8 text-right shrink-0" title="Visualizzazioni di pagina">{d.count}</span>
                    <span className="w-14 text-right shrink-0 text-taupe" title="Sessioni (id anonimo di sessione)" data-testid={`analytics-daily-sessions-${d.day}`}>
                      {sessionsByDay[d.day] ? `${sessionsByDay[d.day]} ses.` : "—"}
                    </span>
                  </div>
                ))}
                {!daily.length && <div className="text-taupe text-sm">Nessuna visualizzazione registrata nel periodo.</div>}
              </div>
              {daily.length > 0 && (
                <p className="text-[10px] text-taupe mt-3">Barra e primo numero: visualizzazioni di pagina. Secondo numero: sessioni.</p>
              )}
            </div>
            <div className="bg-white border border-hair p-4 md:p-6 min-w-0">
              <div className="eyebrow mb-4">Pagine più visualizzate</div>
              <ul className="space-y-2" data-testid="analytics-top-pages">
                {(data.top_pages || []).map((p) => (
                  <li key={p.path} className="flex justify-between gap-3 text-sm">
                    <span className="text-taupe font-mono truncate min-w-0">{p.path}</span>
                    <span className="font-serif text-lg shrink-0">{p.count}</span>
                  </li>
                ))}
                {!data.top_pages?.length && <li className="text-taupe text-sm">Nessun dato nel periodo.</li>}
              </ul>
            </div>
            <div className="bg-white border border-hair p-4 md:p-6 min-w-0">
              <div className="eyebrow mb-4">Sorgenti di traffico</div>
              <ul className="space-y-2" data-testid="analytics-sources">
                {sources.map((s) => (
                  <li key={s.source} className="flex justify-between gap-3 text-sm">
                    <span className="text-taupe truncate min-w-0">{s.source}</span>
                    <span className="shrink-0 text-xs"><span className="font-serif text-lg">{s.views}</span> vis. · {s.sessions} ses.</span>
                  </li>
                ))}
                {!sources.length && <li className="text-taupe text-sm">Nessun dato nel periodo.</li>}
              </ul>
              <p className="text-[10px] text-taupe mt-3">
                La sorgente è rilevata all'arrivo (parametri UTM o sito di provenienza) e vale per l'intera sessione.
                "Diretto / nessun referrer" include chi digita l'indirizzo e i browser che non comunicano la provenienza;
                il traffico precedente all'attivazione risulta "Non tracciata (storico)".
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
